import {
  ClickAwayListener,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
  Typography,
} from '@mui/material'
import {
  FC, HTMLAttributes, useCallback, useContext, useMemo, useRef, useState,
} from 'react'
import {useTranslate} from 'react-admin'
import {CSSObject} from 'tss-react'
import {makeStyles} from 'tss-react/mui'

import {GroupedChatMessage} from '../../../hooks/useGroupIncomingMediaMessages'
import {useUserDisplayName} from '../../../hooks/useUserDisplayName'
import {
  WhatsappMessages,
  WhatsappMessageStatusesEnum,
} from '../../../types/graphqlSchema'
import {ChevronDownIcon, DownloadIcon, ShareIcon} from '../../icons'
import {ChatContext} from '../ChatContext'
import ForwardedChatMessageBanner from '../ForwardedChatMessageBanner'
import ChatMessageFileAttachmentList from './ChatMessageFileAttachmentList'
import ChatMessageStatusLine from './ChatMessageStatusLine'
import ChatMessageTemplateActionButtons from './ChatMessageTemplateActionButton'
import ChatMessageText from './ChatMessageText'
import ChatMessageTranslation from './ChatMessageTranslation'
import ChatMessageTranslationBanner from './ChatMessageTranslationBanner'
import RepliedChatMessageBanner from './RepliedChatMessageBanner'
import TemplateHeaderMedia from './TemplateHeaderMedia'

const ChatMessageBubble: FC<ChatMessageBubbleProps & HTMLAttributes<HTMLDivElement>> = ({
  displayText,
  isRight,
  isShowingTranslations,
  onOpenTemplateDialog,
  onResendChatMessage,
  status,
  value,
  ...props
}) => {
  const {
    authorUser,
    chat,
    chatId,
    chatMessageFileAttachments,
    chatMessageTemplate,
    chatMessageTemplateValues,
    chatMessageTranslations,
    chatMessageWhatsappMessages,
    detectedLanguage,
    forwardedChatMessage,
    id,
    lastReactionChatMessage,
    receivingErrorCode,
    repliedChatMessage,
    templateHeaderMediaKey,
    templateHeaderMediaMimeType,
    timestamp,
  } = value
  const isWhatsappMessageTypeUnknown =
    chatMessageWhatsappMessages?.[0]?.whatsappMessage.type === 'UNKNOWN'
  const {classes: styles} = useStyles({
    hasFailure: (status === 'FAILED') || isWhatsappMessageTypeUnknown,
  })
  const fileAttachments = useMemo(
    () => forwardedChatMessage ?
      forwardedChatMessage.chatMessageFileAttachments : chatMessageFileAttachments,
    [chatMessageFileAttachments, forwardedChatMessage]
  )
  const userDisplayName = useUserDisplayName()
  return (
    <div className={styles.root} {...props}>
      {lastReactionChatMessage?.reactionEmoji && (
        <div className={styles.reactionEmoji}>
          {lastReactionChatMessage?.reactionEmoji}
        </div>
      )}
      <ChatMessageActionsButton
        value={{...value, chatMessageFileAttachments, id}}
      />
      <div
        className={[
          styles.chatMessageBubble,
          isRight ? styles.right : styles.left,
          forwardedChatMessage && styles.forwarded,
        ].filter(Boolean).join(' ')}
      >
        {repliedChatMessage && (
          <RepliedChatMessageBanner repliedChatMessage={repliedChatMessage} />
        )}
        {isShowingTranslations && !!chatMessageTranslations.length && (
          <ChatMessageTranslationBanner
            detectedLanguage={detectedLanguage}
            translatedLanguages={chatMessageTranslations.map(({language}) => language)}
          />
        )}
        {forwardedChatMessage && (
          <ForwardedChatMessageBanner forwardedChatMessage={forwardedChatMessage} />
        )}
        <ChatMessageFileAttachmentList
          chatId={chatId}
          fileAttachments={fileAttachments}
          fullWidth={!!displayText}
          isTranslationVisible={isShowingTranslations}
        />
        {templateHeaderMediaKey && (
          <TemplateHeaderMedia
            templateHeaderMediaKey={templateHeaderMediaKey}
            templateHeaderMediaMimeType={templateHeaderMediaMimeType as string}
          />
        )}
        {chatMessageTemplate?.headline && (
          <Typography variant="h6">{chatMessageTemplate?.headline}</Typography>
        )}
        <ChatMessageText
          chatMessageTemplate={chatMessageTemplate}
          chatMessageTemplateValues={chatMessageTemplateValues}
          forwardedChatMessage={forwardedChatMessage}
          text={displayText}
        />
        {chatMessageTemplate?.footer && (
          <Typography variant="body2" className={styles.footer}>
            {chatMessageTemplate?.footer}
          </Typography>
        )}
        {isShowingTranslations && !!chatMessageTranslations.length && (
          <ChatMessageTranslation translations={chatMessageTranslations} />
        )}
        {(
          !!chatMessageTemplate?.buttonText ||
          !!chatMessageTemplate?.chatMessageTemplateButtons?.length
        ) && (
          <ChatMessageTemplateActionButtons
            template={chatMessageTemplate}
            whatsappMessage={chatMessageWhatsappMessages?.find(
              c => c.whatsappMessage.type === 'TEMPLATE'
            )?.whatsappMessage as WhatsappMessages}
          />
        )}
      </div>
      <ChatMessageStatusLine
        authorUserDisplayName={
          (
            isRight || !chat.channelId || authorUser?.channelChatBotUser
          ) ? userDisplayName(authorUser) : undefined
        }
        chatMessageId={id}
        chatMessageReceivingErrorCode={receivingErrorCode}
        failedWhatsappMessage={chatMessageWhatsappMessages?.find(
          c => c.whatsappMessage.status === 'FAILED'
        )?.whatsappMessage}
        insertionTimestamp={timestamp}
        onOpenTemplateDialog={onOpenTemplateDialog}
        onResendChatMessage={onResendChatMessage}
        whatsappMessageReceivingErrorCode={chatMessageWhatsappMessages?.find(
          c => c.whatsappMessage.type === 'UNKNOWN'
        )?.whatsappMessage.receivingErrorCode}
        whatsappMessageStatus={status}
      />
    </div>
  )
}

const ChatMessageActionsButton = ({value}: ChatMessageActionsButtonProps) => {
  const translate = useTranslate()
  const {classes: styles} = useStyles({})
  const [isOpen, setIsOpen] = useState(false)
  const {
    bulkAction,
    isBulkActionsEnabled,
    onSetBulkAction,
    onToggleIsChatMessageSelected,
    setCurrentlyRepliedChatMessage,
  } = useContext(ChatContext)
  const anchorElementRef = useRef<HTMLButtonElement | null>(null)
  const onSelectChatMessage = useCallback(() => {
    if (value.groupedMessages?.length) {
      value.groupedMessages.forEach(msg => onToggleIsChatMessageSelected(msg))
    }
    else {
      onToggleIsChatMessageSelected(value)
    }
  }, [onToggleIsChatMessageSelected, value])
  return (
    <>
      {isBulkActionsEnabled && !bulkAction && (
        <IconButton
          aria-label="chat-message-actions"
          className={styles.chatMessageActionsButton}
          onClick={() => setIsOpen(isOpen_ => !isOpen_)}
          ref={anchorElementRef}
        >
          <ChevronDownIcon size="inherit" />
        </IconButton>
      )}
      <Popper
        anchorEl={anchorElementRef.current}
        modifiers={[
          {enabled: true, name: 'arrow', options: {element: anchorElementRef.current}},
        ]}
        open={isOpen}
        placement="bottom-start"
      >
        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
          <Paper
            className={styles.popperPaper}
            elevation={3}
            onClick={e => e.stopPropagation()}
          >
            <MenuItem
              onClick={() => {
                setIsOpen(false)
                setCurrentlyRepliedChatMessage(value)
              }}
            >
              <ListItemIcon>
                <ShareIcon color="secondary" style={{transform: 'rotateY(180deg)'}} />
              </ListItemIcon>
              <ListItemText>
                <Typography color="primary">{translate('actions.reply')}</Typography>
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setIsOpen(false)
                onSetBulkAction('FORWARD')
                onSelectChatMessage()
              }}
            >
              <ListItemIcon><ShareIcon color="secondary" /></ListItemIcon>
              <ListItemText>
                <Typography color="primary">
                  {translate('actions.forward')}
                </Typography>
              </ListItemText>
            </MenuItem>
            {!!value.chatMessageFileAttachments.length && (
              <MenuItem
                onClick={() => {
                  setIsOpen(false)
                  onSetBulkAction('DOWNLOAD')
                  onSelectChatMessage()
                }}
              >
                <ListItemIcon>
                  <DownloadIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>
                  <Typography color="primary">
                    {translate('actions.download')}
                  </Typography>
                </ListItemText>
              </MenuItem>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  )
}

const useStyles = makeStyles<
  MakeStylesOptions,
  'left' | 'right' | 'forwarded' | 'chatMessageActionsButton'
>()((theme, {hasFailure}, classes) => ({
  chatMessageActionsButton: {
    color: theme.palette.info.main,
    fontSize: theme.typography.body1.fontSize,
    padding: theme.remSpacing(1),
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 1,
  },
  chatMessageBubble: {
    [`&.${classes.forwarded}`]: {
      background: theme.palette.background.default,
    },
    [`&.${classes.left}`]: {
      background: theme.palette.background.paper,
    },
    [`&.${classes.right}`]: {
      background: theme.palette.chatBubble.main,
    },
    borderBottom: hasFailure ? `1px ${theme.palette.error.main} solid` : 'none',
    borderRadius: `8px 8px ${hasFailure ? '2px 2px' : ''}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: theme.remSpacing(2),
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  footer: {
    marginTop: theme.remSpacing(1),
  },
  forwarded: {},
  left: {},
  popperPaper: {
    borderRadius: theme.remSpacing(1.5),
    boxShadow: '-4px 4px 14px 0px rgba(0, 0, 0, 0.15)',
    padding: `${theme.remSpacing(1.5)} 0`,
  },
  reactionEmoji: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.background.secondary}`,
    borderRadius: '50%',
    bottom: theme.remSpacing(2),
    left: theme.remSpacing(1),
    padding: theme.remSpacing(0.5),
    position: 'absolute',
    ...theme.typography.caption as CSSObject,
  },
  right: {},
  root: {
    [theme.breakpoints.up('lg')]: {
      [`&:not(:hover) > .${classes.chatMessageActionsButton}`]: {
        display: 'none',
      },
    },
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 'calc(min(60%, 400px))',
    paddingBottom: theme.typography.pxToRem(5),
    [theme.breakpoints.down('sm')]: {
      maxWidth: 'calc(min(75%, 400px))',
    },
    position: 'relative',
  },
}))

interface ChatMessageBubbleProps {
  displayText: string | undefined
  isRight: boolean
  isShowingTranslations: boolean
  onResendChatMessage: VoidFunction
  onOpenTemplateDialog: VoidFunction,
  status: WhatsappMessageStatusesEnum
  value: GroupedChatMessage
}

interface ChatMessageActionsButtonProps {
  value: GroupedChatMessage
}

interface MakeStylesOptions {
  hasFailure?: boolean
}

export default ChatMessageBubble
